import type { Unsubscribe } from '@hgc/core';

/**
 * 分区（导航）定义。
 *
 * 顺序即界面上的顺序，也是"用户最常待在哪"的顺序：飞行在最上。
 * 每个分区都带一句 `hint` —— 导航项上的 `title` 与将来的命令面板都用它，
 * 免得同一句话在两个地方各写一遍然后慢慢走样。
 */
export type SectionId = 'fly' | 'plan' | 'setup' | 'analyze';

export interface SectionDefinition {
  readonly id: SectionId;
  readonly label: string;
  readonly hint: string;
}

export const SECTIONS: readonly SectionDefinition[] = [
  { id: 'fly', label: '飞行', hint: '实时遥测、飞行操作与地图' },
  { id: 'plan', label: '计划', hint: '航线规划、上传下载与地理围栏' },
  { id: 'setup', label: '设置', hint: '参数、传感器校准、机架与固件' },
  { id: 'analyze', label: '分析', hint: '遥测日志回放与曲线分析' },
];

export const DEFAULT_SECTION: SectionId = 'fly';

function isSectionId(value: string): value is SectionId {
  return SECTIONS.some((section) => section.id === value);
}

export function sectionHref(id: SectionId): string {
  return `#/${id}`;
}

/** `#/plan` / `#plan` / `#/plan/anything` 都认；认不出来返回 null（由调用方给默认值） */
function parseHash(hash: string): SectionId | null {
  const segment = hash.replace(/^#\/?/, '').split(/[/?]/)[0] ?? '';
  const candidate = segment.trim().toLowerCase();
  return isSectionId(candidate) ? candidate : null;
}

/*
 * 路由状态是一个模块级外部 store，理由与 `@hgc/ui` 的主题 store 相同：
 * 它必须能被任意数量的消费方读到同一份值，而它本身不是任何组件的私有状态。
 *
 * 用 URL hash 而不是 react-router：四个分区之间没有嵌套、没有参数、没有路由守卫，
 * 需要的只有"地址栏记住我在哪、刷新回到原处、前进后退能用"这三件事 ——
 * 这三件事 `hashchange` 已经全给了。少一层依赖意味着少一处版本与行为要跟着走。
 */
const listeners = new Set<() => void>();

function notifyRoute(): void {
  for (const listener of [...listeners]) listener();
}

export function getRoute(): SectionId {
  if (typeof window === 'undefined') return DEFAULT_SECTION;
  return parseHash(window.location.hash) ?? DEFAULT_SECTION;
}

export function subscribeRoute(listener: () => void): Unsubscribe {
  listeners.add(listener);
  // 原生监听器全局挂一个：每个消费方各加一个的话，退订时漏一个就永久泄漏
  if (listeners.size === 1 && typeof window !== 'undefined') {
    window.addEventListener('hashchange', notifyRoute);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('hashchange', notifyRoute);
    }
  };
}

/** 当前订阅者数量（泄漏检查用） */
export function routeListenerCount(): number {
  return listeners.size;
}

export function navigate(id: SectionId): void {
  if (typeof window === 'undefined') return;
  const next = sectionHref(id);
  if (window.location.hash !== next) window.location.hash = next;
  /*
   * 有些环境把 `hashchange` 放到下一个任务里才发（jsdom 也是）。等它意味着"点了导航
   * 要过一拍界面才变"，这是能感觉出来的卡顿。这里同步通知一次；值没变时
   * `useSyncExternalStore` 拿到相同的字符串，不会重复渲染。
   */
  notifyRoute();
}

/**
 * 把地址栏里的分区归一化。
 *
 * 认不出来（空 hash、手输错的 hash）时落到"飞行"并**写回地址栏**：
 * 不写回的话，用户在 `#/fly` 上点进"计划"再按后退，会先回到那个无效地址再被弹回飞行，
 * 表现为"后退键失灵"。
 */
export function normalizeRoute(): void {
  if (typeof window === 'undefined') return;
  if (parseHash(window.location.hash) !== null) return;
  window.history.replaceState(null, '', sectionHref(DEFAULT_SECTION));
  notifyRoute();
}
